import { Empty, Typography } from "antd";
import { useTranslation } from "react-i18next";
import type { ReportDescriptorView } from "../../types";
import { matchesReportQuery, REPORT_GROUPS, type ReportGroupKey } from "../../utils/reportGroups";
import { reportDescription, reportTitle } from "../../utils/reportI18n";
import { ReportCard } from "./ReportCard";

const { Text } = Typography;

/** Guruh ichidagi kartalar ustuni (canvas: davomat 2, xavfsizlik 3, dala 2). */
const USTUNLAR: Record<ReportGroupKey | "other", string> = {
	attendance: "md:grid-cols-2",
	security: "md:grid-cols-2 xl:grid-cols-3",
	field: "md:grid-cols-2",
	other: "md:grid-cols-2 xl:grid-cols-3",
};

export interface ReportGroupsProps {
	descriptors: ReportDescriptorView[];
	query: string;
	range: { dateFrom: string; dateTo: string };
}

/**
 * Hisobotlar guruhlari.
 *
 * ⚠️ Kartalar SERVER descriptor ro'yxatidan: guruhda turgan, lekin
 * serverda yo'q kind (ruxsat/versiya farqi) chizilmaydi; serverda bor,
 * lekin `REPORT_GROUPS` da yo'q kind «Boshqa» guruhiga tushadi —
 * yangi hisobot jimgina yo'qolmaydi.
 */
export function ReportGroups({ descriptors, query, range }: ReportGroupsProps) {
	const { t } = useTranslation("reports");
	const mos = descriptors.filter((d) =>
		matchesReportQuery([reportTitle(t, d), reportDescription(t, d)], query)
	);
	const guruhlangan = new Set(REPORT_GROUPS.flatMap((g) => g.kinds as string[]));
	const guruhlar = [
		...REPORT_GROUPS.map((g) => ({
			key: g.key as ReportGroupKey | "other",
			items: g.kinds.flatMap((kind) => mos.filter((d) => d.kind === kind)),
		})),
		{ key: "other" as const, items: mos.filter((d) => !guruhlangan.has(d.kind)) },
	].filter((g) => g.items.length > 0);

	if (guruhlar.length === 0) {
		return <Empty data-search-empty description={t("hub.searchEmpty")} />;
	}

	return (
		<div className="flex flex-col gap-5">
			{guruhlar.map((guruh) => (
				<section
					key={guruh.key}
					data-report-group={guruh.key}
					aria-labelledby={`hub-group-${guruh.key}`}
				>
					<div className="mb-2.5 flex flex-wrap items-baseline gap-2.5">
						<h2 id={`hub-group-${guruh.key}`} className="m-0 font-semibold text-base">
							{t(`hub.groups.${guruh.key}.title`)}
						</h2>
						<Text type="secondary" className="text-xs">
							{t("hub.groups.count", { count: guruh.items.length })}
							{guruh.key === "other" ? "" : ` · ${t(`hub.groups.${guruh.key}.note`)}`}
						</Text>
					</div>
					<div className={`grid grid-cols-1 gap-3.5 ${USTUNLAR[guruh.key]}`}>
						{guruh.items.map((descriptor) => (
							<ReportCard
								key={descriptor.kind}
								descriptor={descriptor}
								group={guruh.key}
								range={range}
							/>
						))}
					</div>
				</section>
			))}
		</div>
	);
}
